// client/src/services/BaseService.js
import telemetryService from './TelemetryService.js'
import flex from '../api/FlexFetch'
import { unwrapStrictData } from '../api/contractUtils'

/**
 * Base service class providing standardized error handling and response formatting
 * Following SRP: Pure service logic - UI notifications moved to component layer
 */
class BaseService {
  constructor(basePath = '') {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || ''
    this.basePath = basePath

    // Performance logging configuration
    this.PERF_WARN_THRESHOLD = 2000 // 2 seconds
    this.PERF_SLOW_THRESHOLD = 5000 // 5 seconds

    // Request deduplication - track in-flight requests
    this._pendingRequests = new Map() // Map<requestKey, Promise>

    // Telemetry integration
    this.telemetry = telemetryService
  }

  /**
   * Standardized error handling for all services
   *
   * In development mode, includes additional debug information
   * to help with troubleshooting while keeping production responses clean.
   *
   * @param {Error} error - The error object
   * @param {string} context - Context of where the error occurred
   * @returns {Object} - Standardized error response with structure:
   *   {
   *     success: false,
   *     error: string,
   *     data: null,
   *     _debug?: {context, originalError, stack, timestamp, response?} // Only in development mode
   *   }
   */
  handleError(error, context = 'Operation') {
    // Determine the error message first
    let errorMessage = 'An unexpected error occurred'

    if (error.response) {
      // API responded with error status
      const responseError =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.data?.error

      if (typeof responseError === 'string') {
        errorMessage = responseError
      } else if (responseError && typeof responseError === 'object') {
        errorMessage =
          responseError.message ||
          responseError.detail ||
          responseError.code ||
          errorMessage
      }
    } else if (error.message) {
      // Network or other error
      errorMessage = error.message
    }

    // Don't log expected "player not in division" errors (normal during registration)
    // TODO: Replace string matching with errorCode-based detection for robustness
    // Current approach is fragile - backend message changes will break suppression
    const isExpectedDivisionError = errorMessage.includes(
      'Player not assigned to any division',
    )

    if (!isExpectedDivisionError) {
      console.error(`${context} error:`, error)
    }

    const errorResponse = {
      success: false,
      error: errorMessage,
      data: null,
    }

    // Include debug information in development mode
    if (process.env.NODE_ENV === 'development') {
      errorResponse._debug = {
        context,
        originalError: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      }

      // Include response details if available
      if (error.response) {
        errorResponse._debug.response = {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        }
      }
    }

    return errorResponse
  }

  /**
   * Standardized success response formatting
   * @param {*} data - The response data
   * @param {string} message - Success message
   */
  formatSuccess(data, message = null) {
    return {
      success: true,
      error: null,
      data,
      message,
    }
  }

  /**
   * Validate required parameters
   *
   * Usage Guidelines:
   * - ALWAYS validate user-provided parameters (IDs, data objects, required fields)
   * - Call this at the START of your execute() block before any API calls
   * - Validates that parameters exist (not null, undefined, or empty string)
   * - Throws error if validation fails - caught by execute() wrapper
   *
   * @param {Object} params - Parameters to validate
   * @param {Array} required - Array of required parameter names
   * @throws {Error} If any required parameters are missing
   *
   * @example
   * async createItem(itemId, data) {
   *   return this.execute(async () => {
   *     this.validateParams({ itemId, data }, ['itemId', 'data'])
   *     // ... proceed with API call
   *   }, 'Create item')
   * }
   */
  validateParams(params, required) {
    const missing = required.filter((param) => !params[param])
    if (missing.length > 0) {
      throw new Error(`Missing required parameters: ${missing.join(', ')}`)
    }
  }

  /**
   * Handle async operations with standardized error handling and performance logging
   * Includes AUTOMATIC request deduplication to prevent duplicate in-flight requests
   *
   * @param {Function} operation - The async operation to execute
   * @param {string} context - Context for error handling (e.g., 'Get my leagues')
   * @param {Object} options - Execution options
   * @param {string|number|null} options.dedupeKey - Key for deduplication (must be primitive).
   *                                                  - undefined (default): Auto-dedupe using context
   *                                                  - string/number: Custom dedupe key
   *                                                  - null: Disable deduplication
   * @returns {Promise<Object>} - Service response object
   *
   * @example
   * // Automatic deduplication (default) - uses context as key
   * await this.execute(async () => api.getMyLeagues(), 'Get my leagues')
   * // Second call with same context returns existing promise
   *
   * // Custom deduplication key (for parameterized requests)
   * await this.execute(
   *   async () => api.getLeague(id),
   *   'Get league',
   *   { dedupeKey: `league-${id}` }
   * )
   *
   * // Disable deduplication (for mutations, time-sensitive data)
   * await this.execute(
   *   async () => api.submitScore(data),
   *   'Submit score',
   *   { dedupeKey: null }
   * )
   *
   * // ❌ WRONG - objects are compared by reference, won't dedupe
   * { dedupeKey: { userId: 1, action: 'submit' } }
   *
   * // ✅ CORRECT - use string/number primitives
   * { dedupeKey: `submit-${userId}` }
   * { dedupeKey: 123 }
   */
  async execute(operation, context = 'Operation', options = {}) {
    let { dedupeKey } = options

    // Auto-generate dedupeKey if not explicitly provided
    // This enables automatic deduplication for most GET requests
    if (dedupeKey === undefined) {
      dedupeKey = context
    }

    // Validate dedupeKey is primitive (string or number)
    if (
      dedupeKey !== undefined &&
      dedupeKey !== null &&
      typeof dedupeKey === 'object'
    ) {
      console.warn(
        `[DEDUPE] dedupeKey must be a primitive (string/number), got ${typeof dedupeKey}. ` +
          `Deduplication will not work correctly. Consider using JSON.stringify() or a unique string identifier.`,
      )
    }

    // Check for duplicate in-flight request
    if (dedupeKey && this._pendingRequests.has(dedupeKey)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[DEDUPE] Returning existing promise for: ${context}`)
      }
      return this._pendingRequests.get(dedupeKey)
    }

    const startTime = performance.now()

    // Create the promise for this request
    const requestPromise = (async () => {
      try {
        const result = await operation()

        // Log performance metrics
        const duration = performance.now() - startTime
        this._logPerformance(context, duration, true)

        // Record telemetry for successful operations
        this._recordTelemetry({
          operation: context,
          service: this.constructor.name,
          duration,
          success: true,
        })

        const normalizedResult = this._normalizeStrictSuccessResult(result)

        return this.formatSuccess(normalizedResult)
      } catch (error) {
        // Log performance even on error
        const duration = performance.now() - startTime
        this._logPerformance(context, duration, false)

        // Record telemetry for failed operations
        this._recordTelemetry({
          operation: context,
          service: this.constructor.name,
          duration,
          success: false,
          error: error.message,
          errorType: error.constructor.name,
        })

        return this.handleError(error, context)
      } finally {
        // Clean up pending request tracking
        if (dedupeKey) {
          this._pendingRequests.delete(dedupeKey)
        }
      }
    })()

    // Track the pending request if deduplication is enabled
    if (dedupeKey) {
      this._pendingRequests.set(dedupeKey, requestPromise)
    }

    return requestPromise
  }

  /**
   * Unwrap a strict server success envelope { success: true, data: ... } to just `data`
   * so that formatSuccess does not double-wrap it. Non-strict payloads pass through unchanged.
   * Delegates to contractUtils.unwrapStrictData to stay in sync with the canonical definition.
   * @private
   * @param {*} result - The raw value returned by the operation passed to execute()
   * @returns {*} The unwrapped data, or the original result if not a strict envelope
   */
  _normalizeStrictSuccessResult(result) {
    return unwrapStrictData(result)
  }

  /**
   * Log performance metrics for service calls
   * @private
   * @param {string} context - Operation context
   * @param {number} duration - Duration in milliseconds
   * @param {boolean} success - Whether operation succeeded
   */
  _logPerformance(context, duration, success) {
    const statusLabel = success ? '✓' : '✗'

    if (duration > this.PERF_SLOW_THRESHOLD) {
      console.warn(
        `${statusLabel} [VERY SLOW] ${context}: ${duration.toFixed(
          2,
        )}ms (threshold: ${this.PERF_SLOW_THRESHOLD}ms)`,
      )
    } else if (duration > this.PERF_WARN_THRESHOLD) {
      console.warn(
        `${statusLabel} [SLOW] ${context}: ${duration.toFixed(
          2,
        )}ms (threshold: ${this.PERF_WARN_THRESHOLD}ms)`,
      )
    } else if (process.env.NODE_ENV === 'development') {
      // Log all operations in development for debugging with sub-millisecond precision
      console.log(`${statusLabel} ${context}: ${duration.toFixed(2)}ms`)
    }
  }

  /**
   * Record telemetry data for service operations
   * Integrates with TelemetryService for structured observability
   * @private
   * @param {Object} data - Telemetry data
   */
  _recordTelemetry(data) {
    // Skip tracking internal API requests - they provide no analytical value
    // Server logs, APM, and error tracking cover API performance monitoring
    // We only want to track actual user behavior (navigation, feature usage, etc.)
    const isApiRequest =
      data.operation?.startsWith('Get ') ||
      data.operation?.startsWith('Update ') ||
      data.operation?.startsWith('Delete ') ||
      data.operation?.startsWith('Create ')

    if (isApiRequest) {
      return
    }

    try {
      // Send to telemetry service - silently fail if unavailable
      this.telemetry?.trackServiceCall(data)
    } catch (error) {
      // Telemetry errors should never affect application functionality
      // In development, log telemetry issues to aid debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Telemetry error in BaseService._recordTelemetry:', error)
      }
      // Silently ignore errors in production
    }
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint (relative to basePath)
   * @param {Object} body - Request body
   * @returns {Promise<*>} Response data
   */
  async post(endpoint, body = {}) {
    const startTime = performance.now()
    const url = `${this.basePath}${endpoint}`

    try {
      const response = await flex.flexFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await flex.handleResponse(response)

      const duration = performance.now() - startTime
      this._logPerformance(`POST ${endpoint}`, duration, true)

      return data
    } catch (error) {
      const duration = performance.now() - startTime
      this._logPerformance(`POST ${endpoint}`, duration, false)
      throw error
    }
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint (relative to basePath)
   * @param {Object} params - Query parameters
   * @returns {Promise<*>} Response data
   */
  async get(endpoint, params = {}) {
    const startTime = performance.now()
    const url = `${this.basePath}${endpoint}`
    const queryString = Object.keys(params).length
      ? '?' +
        Object.entries(params)
          .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
          .join('&')
      : ''

    try {
      const response = await flex.flexFetch(url + queryString)
      const data = await flex.handleResponse(response)

      const duration = performance.now() - startTime
      this._logPerformance(`GET ${endpoint}`, duration, true)

      return data
    } catch (error) {
      const duration = performance.now() - startTime
      this._logPerformance(`GET ${endpoint}`, duration, false)
      throw error
    }
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint (relative to basePath)
   * @param {Object} body - Request body
   * @returns {Promise<*>} Response data
   */
  async put(endpoint, body = {}) {
    const startTime = performance.now()
    const url = `${this.basePath}${endpoint}`

    try {
      const response = await flex.flexFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await flex.handleResponse(response)

      const duration = performance.now() - startTime
      this._logPerformance(`PUT ${endpoint}`, duration, true)

      return data
    } catch (error) {
      const duration = performance.now() - startTime
      this._logPerformance(`PUT ${endpoint}`, duration, false)
      throw error
    }
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint (relative to basePath)
   * @returns {Promise<*>} Response data
   */
  async delete(endpoint) {
    const startTime = performance.now()
    const url = `${this.basePath}${endpoint}`

    try {
      const response = await flex.flexFetch(url, {
        method: 'DELETE',
      })
      const data = await flex.handleResponse(response)

      const duration = performance.now() - startTime
      this._logPerformance(`DELETE ${endpoint}`, duration, true)

      return data
    } catch (error) {
      const duration = performance.now() - startTime
      this._logPerformance(`DELETE ${endpoint}`, duration, false)
      throw error
    }
  }
}

export default BaseService
